import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';

const EVENT_TYPES = [
  { key: 'meeting', label: 'Meeting', color: '#E6F1FB', text: '#0C447C' },
  { key: 'call', label: 'Call', color: '#EAF3DE', text: '#27500A' },
  { key: 'virtual_call', label: 'Virtual Call', color: '#FAEEDA', text: '#633806' },
];

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function CalendarPage() {
  const { user } = useAuth();
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [selectedDay, setSelectedDay] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterOwner, setFilterOwner] = useState(user?.id || '');

  const emptyForm = {
    title:'', type:'meeting', status:'open',
    start_time:'', end_time:'', notes:'',
    contact_id:'', company_id:'', deal_id:''
  };
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    api.get(`/events?owner_id=${filterOwner}&month=${currentMonth + 1}&year=${currentYear}`)
      .then(r => setEvents(r.data.events || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [currentMonth, currentYear, filterOwner]);

  useEffect(() => {
    api.get('/accounts?limit=100').then(r => setAccounts(r.data.accounts || [])).catch(() => {});
    api.get('/contacts?limit=200').then(r => setContacts(r.data.contacts || [])).catch(() => {});
    api.get('/users').then(r => setUsers(r.data.users || [])).catch(() => {});
    // Get all deals for linking
    api.get('/deals').then(r => {
      const all = [];
      Object.values(r.data.deals || {}).forEach(arr => all.push(...arr));
      setDeals(all);
    }).catch(() => {});
  }, []);

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    setForm({ ...emptyForm, start_time: `${dateStr}T09:00`, end_time: `${dateStr}T10:00` });
    setEditingEvent(null);
    setShowForm(true);
  };

  const handleEditEvent = (e, event) => {
    e.stopPropagation();
    setEditingEvent(event);
    setForm({
      title: event.title,
      type: event.type,
      status: event.status,
      start_time: event.start_time?.slice(0,16) || '',
      end_time: event.end_time?.slice(0,16) || '',
      notes: event.notes || '',
      contact_id: event.contact_id || '',
      company_id: event.company_id || '',
      deal_id: event.deal_id || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return alert('Event title is required.');
    if (!form.start_time) return alert('Start time is required.');
    setSaving(true);
    try {
      if (editingEvent) {
        await api.patch(`/events/${editingEvent.id}`, form);
      } else {
        await api.post('/events', form);
      }
      setShowForm(false);
      setEditingEvent(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save event.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    await api.delete(`/events/${eventId}`);
    setShowForm(false);
    setEditingEvent(null);
    load();
  };

  const handleStatusToggle = async (event) => {
    const newStatus = event.status === 'open' ? 'closed' : 'open';
    await api.patch(`/events/${event.id}`, { status: newStatus });
    load();
  };

  // Build calendar grid
  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let i = 1; i <= daysInMonth; i++) cells.push(i);

  const getEventsForDay = (day) => {
    if (!day) return [];
    return events.filter(e => {
      const d = new Date(e.start_time);
      return d.getDate() === day && d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    });
  };

  const typeInfo = (type) => EVENT_TYPES.find(t => t.key === type) || EVENT_TYPES[0];

  const isToday = (day) => day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear();

  return (
    <>
      {/* Toolbar */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button className="btn" onClick={prevMonth}>←</button>
            <div style={{ fontSize:15, fontWeight:500, minWidth:160, textAlign:'center' }}>
              {MONTHS[currentMonth]} {currentYear}
            </div>
            <button className="btn" onClick={nextMonth}>→</button>
          </div>
          <button className="btn" onClick={() => { setCurrentMonth(today.getMonth()); setCurrentYear(today.getFullYear()); }}>
            Today
          </button>
          <select
            value={filterOwner}
            onChange={e => setFilterOwner(e.target.value)}
            style={{ height:32, padding:'0 10px', border:'0.5px solid var(--border-md)',
                     borderRadius:'var(--radius)', background:'var(--bg-secondary)',
                     color:'var(--text)', fontSize:13, outline:'none' }}
          >
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </select>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <div style={{ display:'flex', border:'0.5px solid var(--border-md)', borderRadius:'var(--radius)', overflow:'hidden' }}>
            <button onClick={() => setViewMode('calendar')}
              style={{ height:32, padding:'0 12px', fontSize:13, cursor:'pointer', border:'none',
                       background: viewMode === 'calendar' ? 'var(--accent)' : 'var(--bg-secondary)',
                       color: viewMode === 'calendar' ? '#fff' : 'var(--text-secondary)' }}>
              Calendar
            </button>
            <button onClick={() => setViewMode('list')}
              style={{ height:32, padding:'0 12px', fontSize:13, cursor:'pointer', border:'none',
                       background: viewMode === 'list' ? 'var(--accent)' : 'var(--bg-secondary)',
                       color: viewMode === 'list' ? '#fff' : 'var(--text-secondary)' }}>
              List
            </button>
          </div>
          <button className="btn btn-primary" onClick={() => {
            setEditingEvent(null);
            const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
            setForm({ ...emptyForm, start_time:`${dateStr}T09:00`, end_time:`${dateStr}T10:00` });
            setShowForm(true);
          }}>
            + New event
          </button>
        </div>
      </div>

      {/* Event Form */}
      {showForm && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="section-title" style={{ marginBottom:14 }}>
            {editingEvent ? 'Edit event' : 'New event'}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <div style={{ gridColumn:'span 2' }}>
              <label className="form-label">Event title *</label>
              <input className="form-input" value={form.title}
                onChange={e => setForm({...form, title:e.target.value})}
                placeholder="e.g. Product demo with Harwin" />
            </div>
            <div>
              <label className="form-label">Type</label>
              <select className="form-input" value={form.type}
                onChange={e => setForm({...form, type:e.target.value})}>
                {EVENT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Start time *</label>
              <input className="form-input" type="datetime-local" value={form.start_time}
                onChange={e => setForm({...form, start_time:e.target.value})} />
            </div>
            <div>
              <label className="form-label">End time</label>
              <input className="form-input" type="datetime-local" value={form.end_time}
                onChange={e => setForm({...form, end_time:e.target.value})} />
            </div>
            <div>
              <label className="form-label">Status</label>
              <select className="form-input" value={form.status}
                onChange={e => setForm({...form, status:e.target.value})}>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="form-label">Link to account</label>
              <select className="form-input" value={form.company_id}
                onChange={e => setForm({...form, company_id:e.target.value})}>
                <option value="">— None —</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Link to contact</label>
              <select className="form-input" value={form.contact_id}
                onChange={e => setForm({...form, contact_id:e.target.value})}>
                <option value="">— None —</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Link to opportunity</label>
              <select className="form-input" value={form.deal_id}
                onChange={e => setForm({...form, deal_id:e.target.value})}>
                <option value="">— None —</option>
                {deals.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div style={{ gridColumn:'span 3' }}>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes}
                onChange={e => setForm({...form, notes:e.target.value})}
                placeholder="Optional notes" />
            </div>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editingEvent ? 'Save changes' : 'Create event'}
            </button>
            {editingEvent && (
              <button className="btn" onClick={() => handleDelete(editingEvent.id)}
                style={{ color:'var(--danger)' }}>
                Delete
              </button>
            )}
            <button className="btn" onClick={() => { setShowForm(false); setEditingEvent(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* CALENDAR VIEW */}
      {viewMode === 'calendar' && (
        <div className="card" style={{ padding:0 }}>
          {/* Day headers */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', borderBottom:'0.5px solid var(--border)' }}>
            {DAYS.map(d => (
              <div key={d} style={{ padding:'8px 0', textAlign:'center', fontSize:11,
                                     fontWeight:600, color:'var(--text-tertiary)', textTransform:'uppercase' }}>
                {d}
              </div>
            ))}
          </div>
          {/* Calendar cells */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)' }}>
            {cells.map((day, i) => {
              const dayEvents = getEventsForDay(day);
              return (
                <div key={i}
                  onClick={() => day && handleDayClick(day)}
                  style={{
                    minHeight:100, padding:'6px 8px',
                    borderRight: (i + 1) % 7 !== 0 ? '0.5px solid var(--border)' : 'none',
                    borderBottom:'0.5px solid var(--border)',
                    background: day ? 'var(--bg)' : 'var(--bg-secondary)',
                    cursor: day ? 'pointer' : 'default',
                  }}
                  onMouseEnter={e => { if (day) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                  onMouseLeave={e => { if (day) e.currentTarget.style.background = 'var(--bg)'; }}
                >
                  {day && (
                    <>
                      <div style={{
                        fontSize:12, fontWeight: isToday(day) ? 700 : 400,
                        color: isToday(day) ? '#fff' : 'var(--text-secondary)',
                        background: isToday(day) ? 'var(--accent)' : 'transparent',
                        width:22, height:22, borderRadius:'50%',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        marginBottom:4
                      }}>
                        {day}
                      </div>
                      {dayEvents.slice(0,3).map(ev => (
                        <div key={ev.id}
                          onClick={e => handleEditEvent(e, ev)}
                          style={{
                            fontSize:11, padding:'2px 5px', borderRadius:3, marginBottom:2,
                            background: typeInfo(ev.type).color, color: typeInfo(ev.type).text,
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                            opacity: ev.status === 'closed' ? 0.6 : 1,
                            textDecoration: ev.status === 'closed' ? 'line-through' : 'none',
                            cursor:'pointer'
                          }}>
                          {new Date(ev.start_time).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})} {ev.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>+{dayEvents.length - 3} more</div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <div className="card" style={{ padding:0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Event</th><th>Type</th><th>Date & time</th>
                <th>Account</th><th>Contact</th><th>Opportunity</th>
                <th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0
                ? <tr><td colSpan={8} style={{ padding:24, color:'var(--text-tertiary)', textAlign:'center' }}>
                    No events this month.
                  </td></tr>
                : events.map(ev => (
                    <tr key={ev.id}>
                      <td style={{ fontWeight:500 }}>{ev.title}</td>
                      <td>
                        <span className="badge" style={{ background: typeInfo(ev.type).color, color: typeInfo(ev.type).text }}>
                          {typeInfo(ev.type).label}
                        </span>
                      </td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>
                        {new Date(ev.start_time).toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}
                      </td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{ev.company_name || '—'}</td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{ev.contact_name || '—'}</td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{ev.deal_name || '—'}</td>
                      <td>
                        <span
                          onClick={() => handleStatusToggle(ev)}
                          className={`badge ${ev.status === 'open' ? 'badge-green' : 'badge-gray'}`}
                          style={{ cursor:'pointer' }}
                          title="Click to toggle status"
                        >
                          {ev.status}
                        </span>
                      </td>
                      <td>
                        <button onClick={e => handleEditEvent(e, ev)}
                          style={{ background:'none', border:'none', cursor:'pointer',
                                   color:'var(--accent-dark)', fontSize:12 }}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div style={{ display:'flex', gap:12, marginTop:12 }}>
        {EVENT_TYPES.map(t => (
          <div key={t.key} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-secondary)' }}>
            <div style={{ width:12, height:12, borderRadius:2, background:t.color, border:`0.5px solid ${t.text}` }}></div>
            {t.label}
          </div>
        ))}
      </div>
    </>
  );
}